import {TKey, useTranslation} from '@/translations/i18n'
import {Controller, useForm} from 'react-hook-form'
import {useEffect} from 'react'
import {useNavigate} from 'react-router'
import {HARD_CANCELLATION_REASONS, SOFT_CANCELLATION_REASONS, CANCELLATION_TYPE_OPTIONS} from '../constants'
import {zodResolver} from '@hookform/resolvers/zod'
import {z} from 'zod'
import {useURLParsedParams} from 'src/hooks/useURLParsedParams.ts'
import {AssignmentUrlParams, ServiceType} from 'src/features/assignment/types.ts'
import {useAssignment} from '@features/assignment/queries/useAssignment'
import {getServiceType} from '@features/assignment/utils.tsx'
import {raise, noUnsupportedChars} from '@utilities/helpers'
import {ModalTitle} from '@components/ui/modal-atoms'
import {Button} from '@components/ui/button'
import {Radio, RadioGroup} from '@components/commons/radio'
import {Spinner} from '@components/ui/spinner'
import {useCancelReasons} from '@features/admin/queries/useCancelReasons'
import {useDeleteAssignment} from '@features/admin/queries/useDeleteAssignment'
import {toast} from '@utilities/toast'
import {routes} from '@utilities/constants/routes'
import {Dialog, DialogContent} from '@components/ui/dialog'
import {TextArea} from '@components/commons/textarea'

const CancelAssignmentModalSchema = z
    .object({
        cancellation_type: z.enum(['soft_cancellation', 'hard_cancellation']),
        reason: z.string(),
        notes: noUnsupportedChars(z.string().optional())
    })
    .superRefine((values, context) => {
        if (values.reason == 'other' && !values.notes) {
            context.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'errors:required',
                path: ['notes']
            })
        }
    })
type CancelAssignmentModalSchema = z.infer<typeof CancelAssignmentModalSchema>

const INPUT_SECTION_WRAPPER_CLASSNAME = 'p-6 flex flex-col gap-3 border-b border-neutral-100'
const SUBTITLE_CLASSNAME = 'text-md font-semibold text-neutral-900'

export const CancelAssignmentModal = ({onClose}: {onClose: () => void}) => {
    const {t} = useTranslation()
    const navigate = useNavigate()
    const urlParams = useURLParsedParams(AssignmentUrlParams)
    const assignmentQuery = useAssignment(urlParams.success ? urlParams.data.code : undefined)
    const onSuccessDeleteAssignment = () => {
        toast.success(t('admin:assignment_cancellation:cancellation_confirmed'))
        onClose()
        navigate(routes.HOME.handle.fullPath)
    }
    const useDeleteAssignmentMutation = useDeleteAssignment({onSuccess: onSuccessDeleteAssignment})
    const reasonsQuery = useCancelReasons()
    const reasons = reasonsQuery.data
    const assignment = assignmentQuery.data
    const rawServiceType = assignment && getServiceType(assignment.task_media.name, assignment.task_category.name)
    const serviceType: ServiceType =
        rawServiceType == 'review' || rawServiceType == 'matterport' || rawServiceType == 'walkthrough'
            ? rawServiceType
            : 'home'

    const form = useForm<CancelAssignmentModalSchema>({
        mode: 'all',
        resolver: zodResolver(CancelAssignmentModalSchema)
    })

    const cancellationType = form.watch('cancellation_type')
    const selectedReason = form.watch('reason')
    const isOtherSelected = selectedReason == 'other'

    const reasonsOptions =
        cancellationType == 'soft_cancellation' ? SOFT_CANCELLATION_REASONS : HARD_CANCELLATION_REASONS
    useEffect(() => {
        form.resetField('reason')
        form.resetField('notes')
    }, [cancellationType])

    const handleSubmit = form.handleSubmit((values: CancelAssignmentModalSchema) => {
        if (!assignmentQuery.isSuccess) {
            return raise('assignment is undefined')
        }
        const cancellationReasonId =
            reasons?.find(reason => reason.key_name == `reasons.${values.reason}`)?.id ??
            raise('cancellationReasonId is undefined')
        useDeleteAssignmentMutation.mutate({
            reason_id: cancellationReasonId,
            cancellation_type: values.cancellation_type,
            notes: values.notes,
            assignmentId: assignmentQuery.data.id
        })
    })

    return (
        <Dialog open={true} onOpenChange={onClose}>
            <DialogContent className="w-145" showCloseButton={false}>
                <form onSubmit={handleSubmit}>
                    <ModalTitle className="p-6">{t('admin:assignment_cancellation:cancel_assignment')}</ModalTitle>
                    <div className="overflow-auto max-h-162.5">
                        <div className={INPUT_SECTION_WRAPPER_CLASSNAME}>
                            <h4 className={SUBTITLE_CLASSNAME}>
                                {t('admin:assignment_cancellation:cancellation_type')}
                            </h4>
                            <Controller
                                control={form.control}
                                name="cancellation_type"
                                render={({field: {onChange, value}}) => (
                                    <RadioGroup
                                        onValueChange={(value: string) => onChange(value)}
                                        value={value !== undefined ? value : ''}
                                        className="flex gap-11"
                                    >
                                        {CANCELLATION_TYPE_OPTIONS.map(item => (
                                            <Radio
                                                value={item.value}
                                                label={t(item.label)}
                                                helpText={t(item.helperText)}
                                                id={item.value}
                                                key={item.value}
                                            />
                                        ))}
                                    </RadioGroup>
                                )}
                            />
                        </div>
                        {!!cancellationType && (
                            <>
                                <div className={INPUT_SECTION_WRAPPER_CLASSNAME}>
                                    <h4 className={SUBTITLE_CLASSNAME}>{t('admin:assignment_cancellation:reason')}</h4>
                                    <Controller
                                        control={form.control}
                                        name="reason"
                                        render={({field: {onChange, value}}) => (
                                            <RadioGroup
                                                onValueChange={(value: string) => onChange(value)}
                                                value={value !== undefined ? value : ''}
                                                className="flex flex-col gap-3"
                                            >
                                                {reasonsOptions.map(item => (
                                                    <Radio
                                                        value={item}
                                                        label={t(
                                                            (item == 'host_no_longer_interested' ||
                                                            item == 'not_comfortable' ||
                                                            item == 'the_listing_has_been_already_photographed'
                                                                ? `admin:assignment_cancellation:${item}:${serviceType}`
                                                                : `admin:assignment_cancellation:${item}`) as TKey
                                                        )}
                                                        id={item}
                                                        key={item}
                                                    />
                                                ))}
                                            </RadioGroup>
                                        )}
                                    />

                                    {isOtherSelected && (
                                        <div className="flex flex-col gap-1 pt-3 pr-6">
                                            <label className="text-sm font-semibold text-neutral-400">{`${t('admin:assignment_cancellation:other_reason')}*`}</label>
                                            <TextArea
                                                placeholder={t('admin:assignment_cancellation:write_here_reason')}
                                                {...form.register('notes')}
                                                errorMessage={t((form.formState.errors?.notes?.message ?? '') as TKey)}
                                            />
                                        </div>
                                    )}
                                </div>

                                {!!selectedReason && !isOtherSelected && (
                                    <div className={INPUT_SECTION_WRAPPER_CLASSNAME}>
                                        <div className="flex flex-col gap-1">
                                            <h4 className={SUBTITLE_CLASSNAME}>{t('commons:notes')}</h4>
                                            <TextArea
                                                placeholder={t('admin:assignment_cancellation:write_here_notes')}
                                                {...form.register('notes')}
                                                errorMessage={t((form.formState.errors?.notes?.message ?? '') as TKey)}
                                            />
                                        </div>
                                    </div>
                                )}
                            </>
                        )}
                    </div>

                    <div className="flex justify-end gap-2.5 p-6">
                        <Button onClick={onClose} variant="tertiary" disabled={useDeleteAssignmentMutation.isPending}>
                            {t('commons:cancel')}
                        </Button>
                        <Button
                            type="submit"
                            disabled={!form.formState.isValid || useDeleteAssignmentMutation.isPending}
                        >
                            {t('commons:confirm')}
                            {useDeleteAssignmentMutation.isPending && <Spinner />}
                        </Button>
                    </div>
                </form>
            </DialogContent>
        </Dialog>
    )
}
